import { useEffect, useRef } from 'react';
import { useToasts } from 'react-toast-notifications';
import { useDispatch, useSelector } from 'react-redux';

import {
  doAddNotification,
  doGetTotalChatCount,
} from 'store/actionCreators/profile';
import {
  doRequestCommunities,
  doUpdateCommunity,
  doRequestCommunityInvites,
} from 'store/actionCreators/communities';
import {
  doCreateOverallEventReply,
  doUpdateOverallEventReply,
  doDeleteOverallEventReply,
  doCreateEventReply,
  doUpdateEventReply,
  doDeleteEventReply,
  doCreateCommunityEvent,
  doUpdateCommunityEvents,
  doDeleteCommunityEvents,
  doCreateEvent,
  doUpdateEvents,
  doDeleteEvents,
  doCreateCommunityTask,
  doEditCommunityTask,
  doDeleteCommunityTask,
  doChangeUserProfile,
  doRequestCurrentCommunity,
} from 'store/actionCreators/currentCommunity';
import {
  doCreateGroup,
  doDeleteGroup,
  doUpdateGroup,
} from 'store/actionCreators/groups';
import { getCurrentCommunity } from 'store/selectors/currentCommunity';
import { getAuthUser, getCurrentIdToken } from 'store/selectors/auth';
import { selectCurrentUserSubscription } from 'store/selectors/subscriptions';
import { messageTypes, typeToRoutes } from 'utils';
import { syncSubscriptionFromWsEvent } from 'utils/subscriptionSync';
import { getInAppToastAppearance } from 'utils/notificationAppearance';
import { Community } from 'types';
import {
  doSendMessage,
  doUpdateUserList,
  doShowMessageNotification,
  doUpdateReadStatus,
} from 'store/actionCreators/chat';

const INITIAL_RECONNECT_MS = 1000;
const MAX_RECONNECT_MS = 30000;

const NotificationsListener = () => {
  const { addToast, removeAllToasts } = useToasts();
  const dispatch = useDispatch();
  const user = useSelector(getAuthUser);
  const socketRef = useRef<WebSocket | null>(null);
  const currentCommunity = useSelector(getCurrentCommunity);
  const currentUserSubscription = useSelector(selectCurrentUserSubscription);
  const idToken = useSelector(getCurrentIdToken);
  const readStatusTimeoutRef = useRef<number | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectAttemptRef = useRef(0);
  const disposedRef = useRef(false);
  const idTokenRef = useRef(idToken);
  const handleMessageRef = useRef<(message: string) => void>(() => undefined);

  const sendMessageToSocket = (data) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(data));
    }
  };

  const currentCommunityRef = useRef<Community>();
  const currentUserSubscriptionRef = useRef(currentUserSubscription);

  const addNotification = async (notification) => {
    await dispatch(doAddNotification(notification));
    const { type } = notification;
    const appearance = getInAppToastAppearance(notification.notificationType);

    switch (type) {
      case messageTypes.COMMUNITY_INVITE_APPROVED: {
        addToast(notification, {
          appearance,
          autoDismiss: true,
        });
        //fetch current community if user is on same community route.
        const isRouteWithCurrentCommunityId = window.location.href
          .split('/')
          .includes(currentCommunityRef.current.id);

        if (isRouteWithCurrentCommunityId)
          return (() => {
            dispatch(
              doRequestCurrentCommunity({ communityId: currentCommunity.id }),
            );
            dispatch(doRequestCommunities());
          })();
        return dispatch(doRequestCommunities());
      }
      case messageTypes.CANCEL_SUBSCRIPTION:
      case messageTypes.SUBSCRIPTION_REMINDER: {
        return addToast(notification, {
          appearance,
          autoDismiss: false,
        });
      }
      case messageTypes.ACTIVATE_SUBSCRIPTION: {
        removeAllToasts();

        return addToast(notification, {
          appearance,
          autoDismiss: false,
        });
      }
      default:
        return addToast(notification, {
          appearance,
          autoDismiss: true,
        });
    }
  };

  const handleMessage = (message) => {
    const data = JSON.parse(message);
    const { type } = data;
    const verificationFunc = typeToRoutes[type];

    if (
      !verificationFunc ||
      !verificationFunc({
        communityId: data?.data?.communityId,
        currentCommunity,
        userId: data?.data?.userId,
      })
    ) {
      return;
    }

    switch (type) {
      case messageTypes.CREATE_COMMUNITY_EVENT_REPLY: {
        return dispatch(
          doCreateOverallEventReply.success(
            Array.isArray(data?.data) ? data.data : [data.data],
          ),
        );
      }
      case messageTypes.CHAT_MESSAGE: {
        const queryParams = new URLSearchParams(window.location.search);
        const communityId = window.location?.pathname?.split('/')[2];
        const talkId = queryParams.get('talkId');
        const peerId = queryParams.get('peerId');
        if (peerId === data?.data.chatId || talkId === data?.data.chatId) {
          dispatch(doSendMessage.success(data?.data));
          // Clear any existing timeout
          if (readStatusTimeoutRef.current) {
            clearTimeout(readStatusTimeoutRef.current);
          }
          // Set new timeout and store the ID
          readStatusTimeoutRef.current = window.setTimeout(() => {
            sendMessageToSocket({
              action: 'message',
              message: {
                chatId: data?.data.chatId,
                messageId: data?.data.message.id,
                userId: user.userId,
                senderId: data?.data.message.senderId,
              },
            });
            readStatusTimeoutRef.current = null;
          }, 2000);
        } else {
          if (!communityId) {
            dispatch(doGetTotalChatCount({ userId: user.userId }));
            dispatch(
              doUpdateUserList.trigger({
                userId: user.userId,
                communityId: currentCommunity.id,
                type: true,
              }),
            );
            dispatch(doShowMessageNotification.success(data?.data));
          } else {
            if (data?.data?.message?.communityId === communityId) {
              dispatch(doGetTotalChatCount({ userId: user.userId }));
              dispatch(
                doUpdateUserList.trigger({
                  userId: user.userId,
                  communityId: currentCommunity.id,
                  type: true,
                }),
              );
              dispatch(doShowMessageNotification.success(data?.data));
            }
          }
        }
        return;
      }
      case messageTypes.READ_STATUS: {
        const queryParams = new URLSearchParams(window.location.search);
        const talkId = queryParams.get('talkId');
        const peerId = queryParams.get('peerId');
        if (peerId === data?.data.chatId || talkId === data?.data.chatId) {
          dispatch(doUpdateReadStatus.success(data?.data));
        }
        return;
      }
      case messageTypes.UPDATE_COMMUNITY_EVENT_REPLY: {
        return dispatch(
          doUpdateOverallEventReply.success(
            Array.isArray(data?.data) ? data.data : [data.data],
          ),
        );
      }
      case messageTypes.DELETE_COMMUNITY_EVENT_REPLY: {
        return dispatch(doDeleteOverallEventReply.success(data.data));
      }
      case messageTypes.CREATE_GROUP_EVENT_REPLY: {
        return dispatch(
          doCreateEventReply.success(
            Array.isArray(data?.data) ? data.data : [data.data],
          ),
        );
      }
      case messageTypes.UPDATE_GROUP_EVENT_REPLY: {
        return dispatch(
          doUpdateEventReply.success({
            response: Array.isArray(data?.data) ? data.data : [data.data],
            userId: Array.isArray(data?.data)
              ? data.data[0].userId
              : data.data.userId,
          }),
        );
      }
      case messageTypes.DELETE_GROUP_EVENT_REPLY: {
        return dispatch(doDeleteEventReply.success(data.data));
      }
      case messageTypes.CREATE_COMMUNITY_EVENT: {
        return dispatch(
          doCreateCommunityEvent.success(
            data.data.map((event) => ({
              ...event,
              subEventReplies: [],
              subEventTaskReplies: [],
            })),
          ),
        );
      }
      case messageTypes.UPDATE_COMMUNITY_EVENT: {
        return dispatch(
          doUpdateCommunityEvents.success({ response: data.data }),
        );
      }
      case messageTypes.DELETE_COMMUNITY_EVENT: {
        return dispatch(
          doDeleteCommunityEvents.success({ ids: data.data.data }),
        );
      }
      case messageTypes.CREATE_GROUP_EVENT: {
        return dispatch(
          doCreateEvent.success(
            data.data.map((event) => ({
              ...event,
              subEventReplies: [],
              subEventTaskReplies: [],
            })),
          ),
        );
      }
      case messageTypes.UPDATE_GROUP_EVENT: {
        return dispatch(doUpdateEvents.success({ response: data.data }));
      }
      case messageTypes.DELETE_GROUP_EVENT: {
        return dispatch(doDeleteEvents.success({ ids: data.data.data }));
      }
      case messageTypes.CREATE_EVENT_TASK: {
        return dispatch(doCreateCommunityTask.success(data.data));
      }
      case messageTypes.UPDATE_EVENT_TASK: {
        return dispatch(doEditCommunityTask.success(data.data));
      }
      case messageTypes.DELETE_EVENT_TASK: {
        return dispatch(doDeleteCommunityTask.success(data.data));
      }
      case messageTypes.CREATE_GROUP: {
        return dispatch(doCreateGroup.success(data.data));
      }
      case messageTypes.UPDATE_GROUP: {
        return dispatch(doUpdateGroup.success(data.data));
      }
      case messageTypes.DELETE_GROUP: {
        const { communityId, groupId } = data.data;
        return dispatch(doDeleteGroup.success({ communityId, groupId }));
      }
      case messageTypes.UPDATE_COMMUNITY: {
        return dispatch(doUpdateCommunity.success(data.data));
      }
      case messageTypes.UPDATE_PROFILE: {
        return dispatch(doChangeUserProfile.success(data.data));
      }
      case messageTypes.GET_COMMUNITY_INVITES:
        return dispatch(doRequestCommunityInvites.success(data.data));
      case messageTypes.ACTIVATE_SUBSCRIPTION:
      case messageTypes.CANCEL_SUBSCRIPTION:
      case messageTypes.SUBSCRIPTION_REMINDER: {
        syncSubscriptionFromWsEvent(
          dispatch,
          currentUserSubscriptionRef.current,
        );
        return addNotification(data);
      }
      default:
        return addNotification(data);
    }
  };

  handleMessageRef.current = handleMessage;
  idTokenRef.current = idToken;

  useEffect(() => {
    currentCommunityRef.current = currentCommunity;
  }, [currentCommunity]);

  useEffect(() => {
    currentUserSubscriptionRef.current = currentUserSubscription;
  }, [currentUserSubscription]);

  useEffect(() => {
    disposedRef.current = false;

    const clearReconnectTimeout = () => {
      if (reconnectTimeoutRef.current != null) {
        window.clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    const detachSocket = (socket: WebSocket | null) => {
      if (!socket) return;
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
    };

    const scheduleReconnect = () => {
      if (disposedRef.current || !idTokenRef.current) return;

      clearReconnectTimeout();
      const attempt = reconnectAttemptRef.current;
      const delay = Math.min(
        INITIAL_RECONNECT_MS * 2 ** attempt,
        MAX_RECONNECT_MS,
      );
      reconnectAttemptRef.current = attempt + 1;

      reconnectTimeoutRef.current = window.setTimeout(() => {
        connect();
      }, delay);
    };

    const connect = () => {
      if (disposedRef.current) return;

      const token = idTokenRef.current;
      if (!token || !process.env.REACT_APP_WS_URL) return;

      clearReconnectTimeout();

      const previous = socketRef.current;
      if (previous) {
        detachSocket(previous);
        if (
          previous.readyState === WebSocket.OPEN ||
          previous.readyState === WebSocket.CONNECTING
        ) {
          previous.close();
        }
        socketRef.current = null;
      }

      const socket = new WebSocket(
        `${process.env.REACT_APP_WS_URL}?Authorization=${encodeURIComponent(
          token,
        )}`,
      );
      socketRef.current = socket;

      socket.onopen = () => {
        reconnectAttemptRef.current = 0;
      };

      socket.onmessage = (event) => {
        handleMessageRef.current(event.data);
      };

      socket.onerror = () => {
        // Browsers always follow onerror with onclose; reconnect there.
      };

      socket.onclose = () => {
        if (socketRef.current === socket) {
          socketRef.current = null;
        }
        if (disposedRef.current) return;
        scheduleReconnect();
      };
    };

    const ensureConnected = () => {
      if (disposedRef.current || !idTokenRef.current) return;
      const socket = socketRef.current;
      if (
        socket &&
        (socket.readyState === WebSocket.OPEN ||
          socket.readyState === WebSocket.CONNECTING)
      ) {
        return;
      }
      reconnectAttemptRef.current = 0;
      connect();
    };

    const handleOnline = () => {
      ensureConnected();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        ensureConnected();
      }
    };

    if (idToken) {
      connect();
    }

    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      disposedRef.current = true;
      clearReconnectTimeout();
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (readStatusTimeoutRef.current != null) {
        window.clearTimeout(readStatusTimeoutRef.current);
        readStatusTimeoutRef.current = null;
      }

      const socket = socketRef.current;
      socketRef.current = null;
      detachSocket(socket);
      if (
        socket &&
        (socket.readyState === WebSocket.OPEN ||
          socket.readyState === WebSocket.CONNECTING)
      ) {
        socket.close();
      }
    };
  }, [idToken]);

  return null;
};

export default NotificationsListener;
